// frontend/src/pages/MeuPerfil.js
// "Meu Perfil": o proprio usuario ve seus dados, troca a foto e altera a senha. Nome, e-mail e cargo so aparecem (quem altera e o administrador).

import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import * as perfilApiPadrao from '../services/perfilApi';
import { prepararImagem as prepararPadrao, TIPOS_FOTO, FOTO_MAX_ARQUIVO } from '../services/imagemPerfil';
import { avisarFotoPerfil } from '../services/fotoPerfil';
import './MeuPerfil.css';

const SENHA_MIN = 8;
const SENHA_MAX_BYTES = 72;

function bytesUtf8(texto) {
  let n = 0;
  for (const ch of texto) { const c = ch.codePointAt(0); n += c < 0x80 ? 1 : c < 0x800 ? 2 : c < 0x10000 ? 3 : 4; }
  return n;
}

export function validarSenha(atual, nova, confirma) {
  if (!atual) return 'Informe a sua senha atual.';
  if (!nova) return 'Informe a nova senha.';
  if (nova.length < SENHA_MIN) return 'A nova senha precisa ter pelo menos ' + SENHA_MIN + ' caracteres.';
  if (bytesUtf8(nova) > SENHA_MAX_BYTES) return 'A nova senha é longa demais (no máximo ' + SENHA_MAX_BYTES + ' caracteres).';
  if (!nova.trim()) return 'A nova senha não pode ser só de espaços.';
  if (nova === atual) return 'A nova senha precisa ser diferente da atual.';
  if (nova !== confirma) return 'A confirmação não é igual à nova senha.';
  return '';
}

export function iniciais(nome) {
  const partes = String(nome || '').trim().split(/\s+/).filter(Boolean);
  if (!partes.length) return '?';
  const a = partes[0].charAt(0);
  const b = partes.length > 1 ? partes[partes.length - 1].charAt(0) : '';
  return (a + b).toUpperCase();
}

const cargo = (r) => { const t = String(r || '').trim(); return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''; };

const Svg = ({ children }) => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{children}</svg>
);
const IconeCamera = () => (<Svg><path d="M14.5 4h-5L8 6H5a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2h-3z" /><circle cx="12" cy="13" r="3.5" /></Svg>);
const IconeLixeira = () => (<Svg><path d="M3 6h18M8 6V4h8v2M6 6l1 14h10l1-14M10 10v6M14 10v6" /></Svg>);
const IconeOlho = () => (<Svg><path d="M2 12s3.5-7 10-7 10 7 10 7-3.5 7-10 7S2 12 2 12z" /><circle cx="12" cy="12" r="3" /></Svg>);
const IconeOlhoRiscado = () => (<Svg><path d="M3 3l18 18M10.6 5.1A9.7 9.7 0 0 1 12 5c6.5 0 10 7 10 7a17 17 0 0 1-3.2 4M6.5 6.6C3.8 8.4 2 12 2 12s3.5 7 10 7c1.6 0 3-.4 4.3-1M9.9 9.9a3 3 0 0 0 4.2 4.2" /></Svg>);

export default function MeuPerfil({ api = perfilApiPadrao, preparar = prepararPadrao }) {
  const auth = useAuth();
  const usuario = auth && auth.user;
  const [perfil, setPerfil] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState('');
  const vivo = useRef(true);
  const arquivoRef = useRef(null);
  const senhaEmVoo = useRef(false);

  const [previa, setPrevia] = useState(null);
  const [processando, setProcessando] = useState(false);
  const [salvandoFoto, setSalvandoFoto] = useState(false);
  const [confirmandoRemocao, setConfirmandoRemocao] = useState(false);
  const [msgFoto, setMsgFoto] = useState(null);

  const [atual, setAtual] = useState('');
  const [nova, setNova] = useState('');
  const [confirma, setConfirma] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [salvandoSenha, setSalvandoSenha] = useState(false);
  const [msgSenha, setMsgSenha] = useState(null);

  useEffect(() => { vivo.current = true; return () => { vivo.current = false; }; }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErroCarga('');
    try {
      const dados = await api.carregarPerfil();
      if (vivo.current) setPerfil(dados);
    } catch (e) {
      if (vivo.current) setErroCarga(e && e.message ? e.message : 'Não foi possível carregar o perfil.');
    } finally {
      if (vivo.current) setCarregando(false);
    }
  }, [api]);
  useEffect(() => { carregar(); }, [carregar]);

  const nome = (perfil && perfil.full_name) || (usuario && (usuario.full_name || usuario.name)) || '';
  const email = (perfil && perfil.email) || (usuario && usuario.email) || '';
  const cargos = ((perfil && perfil.roles) || (usuario && usuario.roles) || []).map(cargo).filter(Boolean);
  const foto = perfil && perfil.avatar_url ? perfil.avatar_url : null;
  const fotoIndisponivel = !!perfil && perfil.fotoDisponivel === false;
  const ocupadoFoto = processando || salvandoFoto;

  const escolherArquivo = async (ev) => {
    const arquivo = ev.target.files && ev.target.files[0];
    ev.target.value = '';
    if (!arquivo) return;
    setMsgFoto(null); setPrevia(null); setConfirmandoRemocao(false);
    if (!TIPOS_FOTO.includes(arquivo.type)) { setMsgFoto({ tipo: 'erro', texto: 'Escolha uma foto JPG, PNG ou WebP.' }); return; }
    if (arquivo.size > FOTO_MAX_ARQUIVO) { setMsgFoto({ tipo: 'erro', texto: 'A foto é grande demais (máximo 10 MB). Escolha uma imagem menor.' }); return; }
    setProcessando(true);
    try {
      const url = await preparar(arquivo);
      if (vivo.current) setPrevia(url);
    } catch (e) {
      if (vivo.current) setMsgFoto({ tipo: 'erro', texto: e && e.amigavel ? e.message : 'Não consegui abrir essa imagem. Tente outro arquivo.' });
    } finally {
      if (vivo.current) setProcessando(false);
    }
  };

  const salvarFoto = async () => {
    if (!previa || ocupadoFoto) return;
    setSalvandoFoto(true); setMsgFoto(null);
    try {
      const r = await api.salvarFoto(previa);
      avisarFotoPerfil((perfil && perfil.id) || (usuario && usuario.id), (r && r.avatar_url) || previa);
      if (vivo.current) { setPerfil((p) => ({ ...(p || {}), avatar_url: (r && r.avatar_url) || previa })); setPrevia(null); setMsgFoto({ tipo: 'ok', texto: 'Foto atualizada.' }); }
    } catch (e) {
      if (vivo.current) setMsgFoto({ tipo: 'erro', texto: e && e.message ? e.message : 'Não foi possível salvar a foto.' });
    } finally {
      if (vivo.current) setSalvandoFoto(false);
    }
  };

  const removerFoto = async () => {
    if (ocupadoFoto) return;
    setSalvandoFoto(true); setMsgFoto(null);
    try {
      await api.removerFoto();
      avisarFotoPerfil((perfil && perfil.id) || (usuario && usuario.id), null);
      if (vivo.current) { setPerfil((p) => ({ ...(p || {}), avatar_url: null })); setPrevia(null); setConfirmandoRemocao(false); setMsgFoto({ tipo: 'ok', texto: 'Foto removida.' }); }
    } catch (e) {
      if (vivo.current) { setConfirmandoRemocao(false); setMsgFoto({ tipo: 'erro', texto: e && e.message ? e.message : 'Não foi possível remover a foto.' }); }
    } finally {
      if (vivo.current) setSalvandoFoto(false);
    }
  };

  const enviarSenha = async (ev) => {
    ev.preventDefault();
    if (senhaEmVoo.current) return;
    const problema = validarSenha(atual, nova, confirma);
    if (problema) { setMsgSenha({ tipo: 'erro', texto: problema }); return; }
    senhaEmVoo.current = true;
    setSalvandoSenha(true); setMsgSenha(null);
    try {
      await api.trocarSenha(atual, nova);
      if (vivo.current) { setAtual(''); setNova(''); setConfirma(''); setMsgSenha({ tipo: 'ok', texto: 'Senha alterada com sucesso. No próximo acesso, use a nova senha.' }); }
    } catch (e) {
      if (vivo.current) { setAtual(''); setMsgSenha({ tipo: 'erro', texto: e && e.message ? e.message : 'Não foi possível trocar a senha.' }); }
    } finally {
      senhaEmVoo.current = false;
      if (vivo.current) setSalvandoSenha(false);
    }
  };

  const tipoSenha = mostrar ? 'text' : 'password';
  const aviso = (msg) => (msg ? <div className={'mp-aviso mp-aviso-' + msg.tipo} role={msg.tipo === 'erro' ? 'alert' : 'status'}>{msg.texto}</div> : null);

  return (
    <div className="mp-pagina">
      <header className="mp-cabecalho">
        <h1>Meu Perfil</h1>
        <p>Gerencie a sua foto e a sua senha.</p>
      </header>

      {erroCarga && (
        <div className="mp-aviso mp-aviso-erro" role="alert">
          {erroCarga} <button type="button" className="mp-link" onClick={carregar}>Tentar de novo</button>
        </div>
      )}

      <section className="mp-card mp-identidade" aria-busy={carregando}>
        <div className="mp-avatar" data-testid="avatar">
          {foto ? <img src={foto} alt="Sua foto de perfil" /> : <span aria-hidden="true">{iniciais(nome)}</span>}
        </div>
        <div className="mp-dados">
          <h2>{carregando && !nome ? 'Carregando…' : nome}</h2>
          <p className="mp-email">{email}</p>
          {cargos.length > 0 && <div className="mp-cargos">{cargos.map((c) => <span key={c} className="mp-cargo">{c}</span>)}</div>}
        </div>
      </section>

      <section className="mp-card" aria-labelledby="mp-foto-titulo">
        <h3 id="mp-foto-titulo">Foto de perfil</h3>
        <p className="mp-ajuda">Use uma foto JPG, PNG ou WebP. Ela é recortada em quadrado e reduzida automaticamente.</p>
        {fotoIndisponivel && <div className="mp-aviso mp-aviso-erro" role="alert">A troca de foto ainda não está disponível neste servidor.</div>}
        <input ref={arquivoRef} type="file" accept="image/jpeg,image/png,image/webp" className="mp-arquivo" aria-label="Escolher foto" onChange={escolherArquivo} disabled={fotoIndisponivel || ocupadoFoto} />
        <div className="mp-acoes">
          <button type="button" className="mp-botao mp-botao-claro" onClick={() => arquivoRef.current && arquivoRef.current.click()} disabled={fotoIndisponivel || ocupadoFoto}>
            <IconeCamera /> {processando ? 'Preparando…' : 'Escolher foto'}
          </button>
          {foto && !previa && !confirmandoRemocao && (
            <button type="button" className="mp-botao mp-botao-perigo" onClick={() => setConfirmandoRemocao(true)} disabled={fotoIndisponivel || ocupadoFoto}>
              <IconeLixeira /> Remover foto
            </button>
          )}
        </div>
        {confirmandoRemocao && (
          <div className="mp-confirma" role="group" aria-label="Confirmar remoção da foto">
            <span>Remover a sua foto?</span>
            <button type="button" className="mp-botao mp-botao-perigo" onClick={removerFoto} disabled={salvandoFoto}>{salvandoFoto ? 'Removendo…' : 'Sim, remover'}</button>
            <button type="button" className="mp-botao mp-botao-claro" onClick={() => setConfirmandoRemocao(false)} disabled={salvandoFoto}>Cancelar</button>
          </div>
        )}
        {previa && (
          <div className="mp-previa">
            <img src={previa} alt="Prévia da nova foto" />
            <div className="mp-acoes">
              <button type="button" className="mp-botao mp-botao-principal" onClick={salvarFoto} disabled={salvandoFoto}>{salvandoFoto ? 'Salvando…' : 'Salvar foto'}</button>
              <button type="button" className="mp-botao mp-botao-claro" onClick={() => setPrevia(null)} disabled={salvandoFoto}>Cancelar</button>
            </div>
          </div>
        )}
        {aviso(msgFoto)}
      </section>

      <section className="mp-card" aria-labelledby="mp-senha-titulo">
        <h3 id="mp-senha-titulo">Alterar senha</h3>
        <p className="mp-ajuda">Use pelo menos {SENHA_MIN} caracteres. Você precisa informar a senha atual.</p>
        <form onSubmit={enviarSenha} noValidate>
          <label className="mp-campo"><span>Senha atual</span>
            <input type={tipoSenha} value={atual} onChange={(e) => setAtual(e.target.value)} autoComplete="current-password" disabled={salvandoSenha} />
          </label>
          <label className="mp-campo"><span>Nova senha</span>
            <input type={tipoSenha} value={nova} onChange={(e) => setNova(e.target.value)} autoComplete="new-password" disabled={salvandoSenha} />
          </label>
          <label className="mp-campo"><span>Confirmar nova senha</span>
            <input type={tipoSenha} value={confirma} onChange={(e) => setConfirma(e.target.value)} autoComplete="new-password" disabled={salvandoSenha} />
          </label>
          <div className="mp-acoes">
            <button type="button" className="mp-botao mp-botao-claro" onClick={() => setMostrar((v) => !v)} aria-pressed={mostrar}>
              {mostrar ? <IconeOlhoRiscado /> : <IconeOlho />} {mostrar ? 'Ocultar senhas' : 'Mostrar senhas'}
            </button>
            <button type="submit" className="mp-botao mp-botao-principal" disabled={salvandoSenha}>{salvandoSenha ? 'Salvando…' : 'Alterar senha'}</button>
          </div>
        </form>
        {aviso(msgSenha)}
      </section>
    </div>
  );
}
